import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FaEllipsisV } from 'react-icons/fa';
import PageHeader from './PageHeader';
import ActionDropdown from './ActionDropdown';

/**
 * DetailPageShell — the standard chrome for a record's detail page (Phase 4):
 * a white header card carrying back button + icon + entity title + status
 * meta + action buttons + a kebab menu, with the page body as children.
 *
 *   <DetailPageShell
 *     title={customer.name}
 *     icon={FaBuilding}
 *     onBack={() => navigate(-1)}
 *     meta={<Badge variant="won">Active</Badge>}
 *     actions={<Button size="sm">Assign to</Button>}
 *     menuItems={[{ label: 'Edit', icon: <MdEdit/>, onClick: ... },
 *                 { label: 'Delete', danger: true, onClick: ... }]}
 *   >
 *     ...tabs / panels...
 *   </DetailPageShell>
 *
 * `menuItems` follows the ActionDropdown items contract; an empty list hides
 * the kebab. Pass `headerExtra` for a second row inside the header card.
 */
const DetailPageShell = ({
  title,
  subtitle,
  icon,
  meta,
  actions,
  menuItems = [],
  menuWidth,
  onBack,
  backTo,
  headerExtra,
  className,
  children
}) => (
  <>
    <div
      className={classNames(
        'tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-shadow-sm tw-px-4 tw-py-3 tw-mb-4',
        className
      )}
    >
      <PageHeader
        variant="plain"
        title={title}
        subtitle={subtitle}
        icon={icon}
        meta={meta}
        onBack={onBack}
        backTo={backTo}
        actions={
          actions || menuItems.length > 0 ? (
            <>
              {actions}
              {menuItems.length > 0 && (
                <ActionDropdown
                  align="end"
                  menuWidth={menuWidth}
                  trigger={<FaEllipsisV size={12} />}
                  items={menuItems}
                />
              )}
            </>
          ) : undefined
        }
      />
      {headerExtra}
    </div>
    {children}
  </>
);

DetailPageShell.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  icon: PropTypes.elementType,
  meta: PropTypes.node,
  actions: PropTypes.node,
  menuItems: PropTypes.array,
  menuWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBack: PropTypes.func,
  backTo: PropTypes.string,
  headerExtra: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node
};

export default DetailPageShell;
